import { useState } from 'react';
import { Layout } from '../components/Layout';

type Section =
  | 'intro'
  | 'dashboard'
  | 'profile'
  | 'chat'
  | 'parameters'
  | 'documents'
  | 'wearable'
  | 'screening'
  | 'export'
  | 'settings';

const sections: { id: Section; label: string; icon: string }[] = [
  { id: 'intro', label: 'Introduzione', icon: '🏠' },
  { id: 'dashboard', label: 'Dashboard', icon: '📊' },
  { id: 'profile', label: 'Profilo', icon: '👤' },
  { id: 'chat', label: 'Consulta', icon: '💬' },
  { id: 'parameters', label: 'Parametri', icon: '📈' },
  { id: 'documents', label: 'Documenti', icon: '📄' },
  { id: 'wearable', label: 'Wearable', icon: '⌚' },
  { id: 'screening', label: 'Screening', icon: '📅' },
  { id: 'export', label: 'Esporta', icon: '💾' },
  { id: 'settings', label: 'Impostazioni', icon: '⚙️' },
];

export function Help() {
  const [activeSection, setActiveSection] = useState<Section>('intro');

  return (
    <Layout title="Guida Utente">
      <div className="max-w-6xl mx-auto">
        {/* Navigation */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-6 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors whitespace-nowrap ${
                  activeSection === section.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <span className="mr-2">{section.icon}</span>
                {section.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-sm p-6 lg:p-8">
          {activeSection === 'intro' && <IntroSection />}
          {activeSection === 'dashboard' && <DashboardSection />}
          {activeSection === 'profile' && <ProfileSection />}
          {activeSection === 'chat' && <ChatSection />}
          {activeSection === 'parameters' && <ParametersSection />}
          {activeSection === 'documents' && <DocumentsSection />}
          {activeSection === 'wearable' && <WearableSection />}
          {activeSection === 'screening' && <ScreeningSection />}
          {activeSection === 'export' && <ExportSection />}
          {activeSection === 'settings' && <SettingsSection />}
        </div>

        {/* Version info */}
        <div className="text-center mt-6 text-sm text-gray-400">
          Health Assistant v1.0.0 - Ultimo aggiornamento guida: Gennaio 2026
        </div>
      </div>
    </Layout>
  );
}

// Reusable components for documentation
function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="text-2xl font-bold text-gray-900 mb-6">{children}</h2>;
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-8">
      <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
        <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
        {title}
      </h3>
      <div className="text-gray-600 leading-relaxed">{children}</div>
    </div>
  );
}

function Tip({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-xl my-4">
      <div className="flex items-start gap-3">
        <span className="text-blue-500 text-xl">💡</span>
        <div className="text-blue-800 text-sm">{children}</div>
      </div>
    </div>
  );
}

function Warning({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-xl my-4">
      <div className="flex items-start gap-3">
        <span className="text-amber-500 text-xl">⚠️</span>
        <div className="text-amber-800 text-sm">{children}</div>
      </div>
    </div>
  );
}

function FeatureList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2 my-4">
      {items.map((item, index) => (
        <li key={index} className="flex items-start gap-3">
          <span className="text-green-500 mt-1">✓</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function StepList({ steps }: { steps: { title: string; description: string }[] }) {
  return (
    <ol className="space-y-4 my-4">
      {steps.map((step, index) => (
        <li key={index} className="flex gap-4">
          <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
            {index + 1}
          </div>
          <div>
            <div className="font-medium text-gray-900">{step.title}</div>
            <div className="text-gray-600 text-sm mt-1">{step.description}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

function HelpImage({ src, alt, caption }: { src: string; alt: string; caption?: string }) {
  return (
    <div className="my-6">
      <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
        <img
          src={src}
          alt={alt}
          className="w-full h-auto"
          loading="lazy"
        />
      </div>
      {caption && (
        <p className="text-center text-sm text-gray-500 mt-2 italic">{caption}</p>
      )}
    </div>
  );
}

// Section Components
function IntroSection() {
  return (
    <div>
      <SectionTitle>Benvenuto in Health Assistant</SectionTitle>

      <p className="text-lg text-gray-600 mb-6">
        Health Assistant è il tuo assistente sanitario personale, progettato per aiutarti a
        gestire e monitorare la tua salute in modo completo e sicuro. Tutti i tuoi dati
        rimangono sul tuo server, garantendo la massima privacy.
      </p>

      <HelpImage
        src="/help/dashboard.png"
        alt="Screenshot della Dashboard principale"
        caption="La Dashboard ti offre una panoramica completa della tua salute"
      />

      <SubSection title="Cosa puoi fare con Health Assistant">
        <FeatureList items={[
          "Consultare un assistente AI per domande sulla tua salute",
          "Caricare e organizzare referti medici e documenti sanitari",
          "Tracciare i parametri di laboratorio nel tempo con grafici interattivi",
          "Collegare dispositivi wearable come Oura Ring e Strava",
          "Gestire il calendario degli screening preventivi",
          "Esportare il tuo fascicolo sanitario in PDF o CSV",
          "Mantenere un profilo sanitario completo con anamnesi e farmaci",
        ]} />
      </SubSection>

      <SubSection title="Primi passi">
        <StepList steps={[
          {
            title: "Completa il tuo profilo",
            description: "Inserisci i tuoi dati anagrafici, anamnesi familiare e farmaci attuali nella sezione Profilo.",
          },
          {
            title: "Carica i tuoi documenti",
            description: "Carica referti medici, analisi del sangue e altri documenti nella sezione Documenti.",
          },
          {
            title: "Inizia una conversazione",
            description: "Usa la sezione Consulta per fare domande all'assistente AI sulla tua salute.",
          },
          {
            title: "Collega i tuoi dispositivi",
            description: "Se hai un Oura Ring o usi Strava, collegali nella sezione Wearable.",
          },
        ]} />
      </SubSection>

      <SubSection title="Privacy e sicurezza">
        <p className="mb-4">
          Health Assistant è stato progettato con la privacy come priorità assoluta:
        </p>
        <FeatureList items={[
          "Tutti i dati sono conservati localmente sul tuo server",
          "Nessun dato viene inviato a terze parti senza il tuo consenso",
          "Le comunicazioni con i servizi LLM sono criptate (HTTPS)",
          "Puoi usare Ollama per un'esperienza completamente locale",
          "Backup e restore del database sotto il tuo controllo",
        ]} />
      </SubSection>

      <SubSection title="Assistenza e Supporto">
        <p className="mb-4">
          Hai bisogno di aiuto o vuoi segnalare un problema? Puoi contattarci via email:
        </p>
        <div className="bg-blue-50 rounded-xl p-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <p className="text-sm text-gray-600">Email di supporto</p>
            <a
              href="mailto:health.assistance.tool@gmail.com"
              className="text-blue-600 font-medium hover:text-blue-700 transition-colors"
            >
              health.assistance.tool@gmail.com
            </a>
          </div>
        </div>
      </SubSection>

      <Tip>
        Naviga tra le sezioni di questa guida usando i pulsanti in alto per scoprire
        tutte le funzionalità disponibili.
      </Tip>
    </div>
  );
}

function DashboardSection() {
  return (
    <div>
      <SectionTitle>Dashboard</SectionTitle>

      <p className="text-gray-600 mb-6">
        La Dashboard è la tua pagina principale, dove puoi vedere a colpo d'occhio
        lo stato della tua salute e accedere rapidamente alle funzionalità più importanti.
      </p>

      <HelpImage
        src="/help/dashboard.png"
        alt="Screenshot della Dashboard"
        caption="La Dashboard mostra una panoramica completa dei tuoi dati sanitari"
      />

      <SubSection title="Panoramica stato di salute">
        <p>
          Nella parte superiore della Dashboard trovi un riepilogo dei tuoi dati più recenti:
        </p>
        <FeatureList items={[
          "Ultimo parametro di laboratorio registrato",
          "Numero di documenti caricati",
          "Prossimo screening in programma",
          "Dati recenti dai dispositivi wearable collegati",
        ]} />
      </SubSection>

      <SubSection title="Widget e statistiche">
        <p>
          I widget sulla Dashboard mostrano:
        </p>
        <FeatureList items={[
          "Grafici dei trend dei parametri principali",
          "Promemoria per screening scaduti o in scadenza",
          "Attività recenti (documenti caricati, conversazioni)",
          "Stato di connessione dei dispositivi wearable",
        ]} />
      </SubSection>

      <SubSection title="Accesso rapido">
        <p>
          Dalla Dashboard puoi accedere rapidamente a:
        </p>
        <FeatureList items={[
          "Nuova conversazione con l'assistente AI",
          "Caricamento di un nuovo documento",
          "Inserimento di un nuovo parametro",
          "Visualizzazione del calendario screening",
        ]} />
      </SubSection>

      <Tip>
        La Dashboard si aggiorna automaticamente quando aggiungi nuovi dati.
        Torna qui regolarmente per avere una visione d'insieme della tua salute.
      </Tip>
    </div>
  );
}

function ProfileSection() {
  return (
    <div>
      <SectionTitle>Profilo Sanitario</SectionTitle>

      <p className="text-gray-600 mb-6">
        Il tuo profilo sanitario contiene tutte le informazioni mediche importanti che
        l'assistente AI utilizza per fornirti risposte personalizzate e accurate.
      </p>

      <HelpImage
        src="/help/profile.png"
        alt="Screenshot della sezione Profilo"
        caption="Il profilo sanitario completo con tutte le informazioni mediche"
      />

      <SubSection title="Dati anagrafici">
        <p>
          Inserisci i tuoi dati personali di base:
        </p>
        <FeatureList items={[
          "Nome e cognome",
          "Data di nascita",
          "Sesso biologico",
          "Altezza e peso (per calcolo BMI)",
          "Gruppo sanguigno",
        ]} />
      </SubSection>

      <SubSection title="Anamnesi familiare">
        <p>
          La storia medica della tua famiglia è importante per valutare i fattori di rischio:
        </p>
        <FeatureList items={[
          "Malattie cardiovascolari in famiglia",
          "Tumori e neoplasie",
          "Diabete",
          "Malattie genetiche",
          "Altre condizioni rilevanti",
        ]} />
        <Tip>
          L'anamnesi familiare aiuta l'assistente AI a suggerirti screening
          preventivi specifici per il tuo profilo di rischio.
        </Tip>
      </SubSection>

      <SubSection title="Condizioni mediche">
        <p>
          Elenca le tue condizioni mediche attuali e passate:
        </p>
        <FeatureList items={[
          "Patologie croniche (diabete, ipertensione, ecc.)",
          "Allergie e intolleranze",
          "Interventi chirurgici passati",
          "Condizioni in corso di trattamento",
        ]} />
      </SubSection>

      <SubSection title="Farmaci e integratori">
        <p>
          Mantieni aggiornato l'elenco dei farmaci che assumi:
        </p>
        <FeatureList items={[
          "Nome del farmaco",
          "Dosaggio",
          "Frequenza di assunzione",
          "Data di inizio terapia",
          "Note aggiuntive",
        ]} />
        <Warning>
          Mantieni sempre aggiornato l'elenco dei farmaci. Queste informazioni sono
          fondamentali per l'assistente AI per evitare interazioni farmacologiche.
        </Warning>
      </SubSection>

      <SubSection title="Stile di vita">
        <p>
          Informazioni sul tuo stile di vita che influenzano la salute:
        </p>
        <FeatureList items={[
          "Abitudine al fumo",
          "Consumo di alcol",
          "Attività fisica",
          "Dieta e alimentazione",
          "Qualità del sonno",
        ]} />
      </SubSection>
    </div>
  );
}

function ChatSection() {
  return (
    <div>
      <SectionTitle>Consulta - Assistente AI</SectionTitle>

      <p className="text-gray-600 mb-6">
        La sezione Consulta ti permette di dialogare con un assistente AI specializzato
        in ambito sanitario, che ha accesso a tutti i tuoi dati per fornirti risposte personalizzate.
      </p>

      <HelpImage
        src="/help/chat.png"
        alt="Screenshot della chat con l'assistente AI"
        caption="L'assistente AI risponde alle tue domande sulla salute"
      />

      <SubSection title="Come funziona">
        <p>
          L'assistente AI ha accesso al tuo profilo sanitario completo, inclusi:
        </p>
        <FeatureList items={[
          "Dati anagrafici e anamnesi",
          "Parametri di laboratorio storici",
          "Documenti caricati (referti, analisi)",
          "Dati dai dispositivi wearable",
          "Farmaci e condizioni mediche",
        ]} />
        <p className="mt-4">
          Questo gli permette di fornirti risposte contestualizzate e specifiche per la tua situazione.
        </p>
      </SubSection>

      <SubSection title="Cosa puoi chiedere">
        <p>
          Esempi di domande che puoi fare:
        </p>
        <FeatureList items={[
          '"I miei valori del colesterolo sono nella norma?"',
          '"Come sono cambiati i miei parametri negli ultimi 6 mesi?"',
          '"Quali screening dovrei fare alla mia età?"',
          '"Ci sono interazioni tra i miei farmaci?"',
          '"Come posso migliorare la qualità del sonno?"',
          '"Cosa significano i risultati delle mie ultime analisi?"',
        ]} />
      </SubSection>

      <SubSection title="Gestione delle conversazioni">
        <p>
          Puoi gestire le tue conversazioni in diversi modi:
        </p>
        <FeatureList items={[
          "Creare nuove conversazioni per argomenti diversi",
          "Continuare conversazioni precedenti",
          "Rinominare le conversazioni per trovarle facilmente",
          "Eliminare conversazioni non più necessarie",
        ]} />
      </SubSection>

      <SubSection title="Allegare documenti">
        <p>
          Durante una conversazione puoi allegare documenti per chiedere spiegazioni:
        </p>
        <StepList steps={[
          {
            title: "Clicca sull'icona allegato",
            description: "Si trova nella barra di input del messaggio.",
          },
          {
            title: "Seleziona il documento",
            description: "Puoi scegliere un documento già caricato o caricarne uno nuovo.",
          },
          {
            title: "Fai la tua domanda",
            description: "L'assistente analizzerà il documento e risponderà alla tua domanda.",
          },
        ]} />
      </SubSection>

      <Warning>
        L'assistente AI non sostituisce il parere del medico. Usa le informazioni
        fornite come supporto, ma consulta sempre un professionista per decisioni mediche importanti.
      </Warning>

      <SubSection title="Provider LLM">
        <p>
          Health Assistant supporta diversi provider per l'intelligenza artificiale:
        </p>
        <FeatureList items={[
          "Ollama - Completamente locale, massima privacy",
          "Anthropic Claude - Potente e accurato",
          "OpenAI GPT - Versatile e affidabile",
        ]} />
        <Tip>
          Se la privacy è la tua priorità, usa Ollama per mantenere tutte le
          conversazioni sul tuo server senza inviarle a servizi esterni.
        </Tip>
      </SubSection>
    </div>
  );
}

function ParametersSection() {
  return (
    <div>
      <SectionTitle>Parametri di Laboratorio</SectionTitle>

      <p className="text-gray-600 mb-6">
        La sezione Parametri ti permette di tracciare e visualizzare l'andamento
        dei tuoi valori di laboratorio nel tempo, con grafici interattivi e confronti storici.
      </p>

      <HelpImage
        src="/help/parameters.png"
        alt="Screenshot della sezione Parametri"
        caption="Grafici interattivi per monitorare i tuoi valori nel tempo"
      />

      <SubSection title="Inserimento manuale">
        <StepList steps={[
          {
            title: "Clicca su 'Aggiungi parametro'",
            description: "Si apre il form per l'inserimento di un nuovo valore.",
          },
          {
            title: "Seleziona il tipo di parametro",
            description: "Scegli dalla lista (es. Glicemia, Colesterolo, Emoglobina).",
          },
          {
            title: "Inserisci il valore e la data",
            description: "Specifica il valore numerico e la data dell'esame.",
          },
          {
            title: "Aggiungi note (opzionale)",
            description: "Puoi aggiungere note come 'a digiuno' o altre indicazioni.",
          },
        ]} />
      </SubSection>

      <SubSection title="Estrazione automatica da documenti">
        <p>
          Quando carichi un referto di analisi del sangue, Health Assistant può
          estrarre automaticamente i parametri:
        </p>
        <FeatureList items={[
          "Carica il PDF delle analisi nella sezione Documenti",
          "L'AI analizza il documento ed estrae i valori",
          "I parametri vengono salvati con la data del referto",
          "Puoi verificare e correggere i valori estratti",
        ]} />
        <Tip>
          L'estrazione automatica funziona meglio con PDF di buona qualità.
          Verifica sempre i valori estratti prima di salvarli.
        </Tip>
      </SubSection>

      <SubSection title="Visualizzazione grafici">
        <p>
          Per ogni parametro puoi visualizzare:
        </p>
        <FeatureList items={[
          "Grafico dell'andamento nel tempo",
          "Range di normalità evidenziato",
          "Valori sopra/sotto la norma colorati diversamente",
          "Media e trend del periodo",
        ]} />
      </SubSection>

      <SubSection title="Parametri supportati">
        <p>
          Health Assistant supporta una vasta gamma di parametri:
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 my-4">
          {[
            'Glicemia', 'Emoglobina', 'Colesterolo totale', 'HDL', 'LDL',
            'Trigliceridi', 'Creatinina', 'Azotemia', 'AST/GOT', 'ALT/GPT',
            'GGT', 'Bilirubina', 'Ferro', 'Ferritina', 'Vitamina D',
            'TSH', 'PSA', 'Acido urico', 'PCR', 'VES',
          ].map((param) => (
            <div key={param} className="bg-gray-50 px-3 py-2 rounded-lg text-sm text-gray-700">
              {param}
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-500 mt-2">
          ...e molti altri. Puoi anche aggiungere parametri personalizzati.
        </p>
      </SubSection>

      <SubSection title="Confronto con i range di normalità">
        <p>
          Ogni parametro viene confrontato con i range di normalità standard:
        </p>
        <FeatureList items={[
          "Verde: valore nella norma",
          "Giallo: valore ai limiti",
          "Rosso: valore fuori norma",
        ]} />
        <Warning>
          I range di normalità sono indicativi. Consulta il tuo medico per
          l'interpretazione corretta dei tuoi valori personali.
        </Warning>
      </SubSection>
    </div>
  );
}

function DocumentsSection() {
  return (
    <div>
      <SectionTitle>Documenti</SectionTitle>

      <p className="text-gray-600 mb-6">
        La sezione Documenti è il tuo archivio digitale per tutti i referti medici,
        le analisi e i documenti sanitari. Carica, organizza e accedi ai tuoi documenti in qualsiasi momento.
      </p>

      <HelpImage
        src="/help/documents.png"
        alt="Screenshot della sezione Documenti"
        caption="L'archivio digitale dei tuoi documenti sanitari"
      />

      <SubSection title="Caricamento documenti">
        <StepList steps={[
          {
            title: "Clicca su 'Carica documento'",
            description: "Puoi trascinare i file o cliccare per selezionarli.",
          },
          {
            title: "Seleziona la categoria",
            description: "Scegli il tipo di documento (analisi, referto, ricetta, ecc.).",
          },
          {
            title: "Aggiungi una descrizione",
            description: "Inserisci un titolo e note per trovare facilmente il documento.",
          },
          {
            title: "Conferma il caricamento",
            description: "Il documento viene salvato e indicizzato per la ricerca.",
          },
        ]} />
      </SubSection>

      <SubSection title="Formati supportati">
        <FeatureList items={[
          "PDF - Referti, analisi, lettere di dimissione",
          "Immagini (JPG, PNG) - Foto di ricette, prescrizioni",
          "Documenti Office - Word, Excel per tabelle dati",
        ]} />
        <Tip>
          I PDF con testo selezionabile sono preferibili alle scansioni,
          perché permettono l'estrazione automatica dei dati.
        </Tip>
      </SubSection>

      <SubSection title="Organizzazione e ricerca">
        <p>
          I documenti possono essere organizzati e trovati facilmente:
        </p>
        <FeatureList items={[
          "Filtro per categoria (analisi, referti, ricette, ecc.)",
          "Filtro per data",
          "Ricerca full-text nel contenuto",
          "Ordinamento per data o nome",
        ]} />
      </SubSection>

      <SubSection title="Visualizzazione e condivisione">
        <p>
          Per ogni documento puoi:
        </p>
        <FeatureList items={[
          "Visualizzare l'anteprima direttamente nel browser",
          "Scaricare il file originale",
          "Allegarlo a una conversazione con l'assistente AI",
          "Includerlo nell'export del fascicolo sanitario",
        ]} />
      </SubSection>

      <SubSection title="Estrazione automatica parametri">
        <p>
          Quando carichi analisi del sangue, l'AI può estrarre automaticamente i parametri:
        </p>
        <FeatureList items={[
          "Riconoscimento automatico dei valori di laboratorio",
          "Salvataggio nella sezione Parametri",
          "Verifica e correzione prima del salvataggio",
        ]} />
      </SubSection>

      <Warning>
        I documenti sono salvati sul tuo server. Assicurati di avere un sistema
        di backup attivo per non perdere i tuoi dati sanitari importanti.
      </Warning>
    </div>
  );
}

function WearableSection() {
  return (
    <div>
      <SectionTitle>Wearable</SectionTitle>

      <p className="text-gray-600 mb-6">
        La sezione Wearable ti permette di collegare i tuoi dispositivi indossabili
        e app fitness per importare automaticamente dati su sonno, attività fisica e altri parametri.
      </p>

      <HelpImage
        src="/help/wearable.png"
        alt="Screenshot della sezione Wearable"
        caption="Collega i tuoi dispositivi per un monitoraggio completo"
      />

      <SubSection title="Dispositivi supportati">
        <div className="grid md:grid-cols-2 gap-6 my-4">
          <div className="bg-gray-50 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">💍</span>
              <h4 className="font-semibold text-gray-900">Oura Ring</h4>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Anello smart per monitoraggio avanzato del sonno e della salute.
            </p>
            <FeatureList items={[
              "Qualità del sonno (fasi, durata)",
              "Variabilità cardiaca (HRV)",
              "Temperatura corporea",
              "Punteggio readiness",
              "Attività quotidiana",
            ]} />
          </div>
          <div className="bg-gray-50 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">🚴</span>
              <h4 className="font-semibold text-gray-900">Strava</h4>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              App per il tracciamento di attività sportive.
            </p>
            <FeatureList items={[
              "Allenamenti (corsa, bici, nuoto)",
              "Distanza e durata",
              "Frequenza cardiaca",
              "Calorie bruciate",
              "Percorsi GPS",
            ]} />
          </div>
        </div>
      </SubSection>

      <SubSection title="Collegare Oura Ring">
        <StepList steps={[
          {
            title: "Vai su cloud.ouraring.com",
            description: "Accedi al tuo account Oura e vai nella sezione Personal Access Tokens.",
          },
          {
            title: "Crea un nuovo token",
            description: "Genera un Personal Access Token con i permessi necessari.",
          },
          {
            title: "Inserisci il token in Health Assistant",
            description: "Nella sezione Wearable > Integrazioni, clicca 'Collega' e inserisci il token.",
          },
          {
            title: "Sincronizza i dati",
            description: "Clicca 'Sincronizza' per importare i tuoi dati.",
          },
        ]} />
      </SubSection>

      <SubSection title="Collegare Strava">
        <p className="mb-4">
          Prima di collegare Strava, l'amministratore deve configurare le credenziali API
          nelle Impostazioni → Integrazioni.
        </p>
        <StepList steps={[
          {
            title: "Clicca 'Collega con Strava'",
            description: "Verrai reindirizzato al sito di Strava.",
          },
          {
            title: "Autorizza l'accesso",
            description: "Accedi con il tuo account Strava e autorizza Health Assistant.",
          },
          {
            title: "Sincronizza i dati",
            description: "Torna su Health Assistant e clicca 'Sincronizza'.",
          },
        ]} />
      </SubSection>

      <SubSection title="Visualizzazione dati">
        <p>
          I dati importati vengono visualizzati in diversi modi:
        </p>
        <FeatureList items={[
          "Tab Metriche: dati giornalieri su sonno e salute",
          "Tab Sonno: dettaglio delle fasi del sonno",
          "Tab Allenamenti: lista delle attività sportive",
          "Tab Obiettivi: monitoraggio degli obiettivi settimanali",
        ]} />
      </SubSection>

      <SubSection title="Import/Export dati">
        <p>
          Puoi anche importare ed esportare dati manualmente:
        </p>
        <FeatureList items={[
          "Import CSV: carica dati da altri dispositivi o app",
          "Export CSV: scarica i tuoi dati per analisi esterne",
          "Inserimento manuale: aggiungi singole metriche o allenamenti",
        ]} />
      </SubSection>

      <Tip>
        Sincronizza regolarmente i tuoi dispositivi per avere dati sempre aggiornati.
        I dati storici sono importanti per identificare trend nel tempo.
      </Tip>
    </div>
  );
}

function ScreeningSection() {
  return (
    <div>
      <SectionTitle>Screening Preventivi</SectionTitle>

      <p className="text-gray-600 mb-6">
        La sezione Screening ti aiuta a tenere traccia degli esami preventivi raccomandati
        per la tua età e il tuo profilo di rischio, con promemoria automatici.
      </p>

      <HelpImage
        src="/help/screening.png"
        alt="Screenshot della sezione Screening"
        caption="Il calendario degli screening preventivi personalizzato"
      />

      <SubSection title="Screening raccomandati">
        <p>
          Health Assistant suggerisce screening basati su:
        </p>
        <FeatureList items={[
          "Età e sesso biologico",
          "Anamnesi familiare (fattori di rischio)",
          "Condizioni mediche esistenti",
          "Linee guida nazionali e internazionali",
        ]} />
      </SubSection>

      <SubSection title="Esempi di screening">
        <div className="grid md:grid-cols-2 gap-4 my-4">
          <div className="bg-blue-50 rounded-xl p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Donna</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Pap test / HPV test</li>
              <li>• Mammografia</li>
              <li>• Densitometria ossea</li>
              <li>• Ecografia pelvica</li>
            </ul>
          </div>
          <div className="bg-green-50 rounded-xl p-4">
            <h4 className="font-semibold text-green-900 mb-2">Uomo</h4>
            <ul className="text-sm text-green-800 space-y-1">
              <li>• PSA (antigene prostatico)</li>
              <li>• Ecografia prostatica</li>
              <li>• Aneurisma aorta addominale</li>
            </ul>
          </div>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 mt-4">
          <h4 className="font-semibold text-purple-900 mb-2">Entrambi</h4>
          <ul className="text-sm text-purple-800 space-y-1">
            <li>• Colonscopia / sangue occulto feci</li>
            <li>• Analisi del sangue complete</li>
            <li>• Visita cardiologica + ECG</li>
            <li>• Visita oculistica</li>
            <li>• Visita dermatologica (nei)</li>
          </ul>
        </div>
      </SubSection>

      <SubSection title="Gestione del calendario">
        <StepList steps={[
          {
            title: "Visualizza gli screening suggeriti",
            description: "La lista mostra gli esami raccomandati per il tuo profilo.",
          },
          {
            title: "Registra uno screening effettuato",
            description: "Clicca sullo screening e inserisci la data dell'esame.",
          },
          {
            title: "Ricevi promemoria",
            description: "Health Assistant ti avvisa quando uno screening è in scadenza.",
          },
          {
            title: "Carica il referto",
            description: "Puoi collegare il documento del referto allo screening.",
          },
        ]} />
      </SubSection>

      <SubSection title="Stati degli screening">
        <div className="flex flex-wrap gap-3 my-4">
          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
            ✓ Completato
          </span>
          <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">
            ⏳ In scadenza
          </span>
          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
            ⚠️ Scaduto
          </span>
          <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
            📅 Programmato
          </span>
        </div>
      </SubSection>

      <Warning>
        Gli screening suggeriti sono basati su linee guida generali. Consulta il tuo
        medico per un piano di prevenzione personalizzato.
      </Warning>
    </div>
  );
}

function ExportSection() {
  return (
    <div>
      <SectionTitle>Esportazione Dati</SectionTitle>

      <p className="text-gray-600 mb-6">
        La sezione Esporta ti permette di creare una copia completa del tuo fascicolo
        sanitario in diversi formati, da condividere con medici o conservare come backup.
      </p>

      <HelpImage
        src="/help/export.png"
        alt="Screenshot della sezione Esporta"
        caption="Esporta il tuo fascicolo sanitario in PDF o CSV"
      />

      <SubSection title="Formati disponibili">
        <div className="grid md:grid-cols-2 gap-6 my-4">
          <div className="bg-red-50 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">📕</span>
              <h4 className="font-semibold text-gray-900">PDF</h4>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Documento formattato pronto da stampare o condividere.
            </p>
            <FeatureList items={[
              "Layout professionale",
              "Grafici dei parametri",
              "Lista documenti allegati",
              "Riepilogo completo",
            ]} />
          </div>
          <div className="bg-green-50 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">📊</span>
              <h4 className="font-semibold text-gray-900">CSV</h4>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Dati grezzi per analisi in Excel o altri software.
            </p>
            <FeatureList items={[
              "Tutti i parametri di laboratorio",
              "Dati wearable",
              "Importabile in Excel",
              "Ideale per analisi",
            ]} />
          </div>
        </div>
      </SubSection>

      <SubSection title="Contenuto dell'export PDF">
        <p>
          Il PDF del fascicolo sanitario include:
        </p>
        <FeatureList items={[
          "Copertina con dati anagrafici",
          "Profilo sanitario completo",
          "Anamnesi familiare e personale",
          "Lista farmaci in corso",
          "Tabella parametri di laboratorio",
          "Grafici trend principali parametri",
          "Riepilogo screening effettuati",
          "Indice dei documenti allegati",
        ]} />
      </SubSection>

      <SubSection title="Come esportare">
        <StepList steps={[
          {
            title: "Seleziona il formato",
            description: "Scegli tra PDF per un documento formattato o CSV per i dati grezzi.",
          },
          {
            title: "Seleziona il periodo",
            description: "Puoi esportare tutti i dati o solo un periodo specifico.",
          },
          {
            title: "Scegli cosa includere",
            description: "Seleziona quali sezioni includere nell'export.",
          },
          {
            title: "Genera e scarica",
            description: "Clicca 'Esporta' e il file verrà scaricato.",
          },
        ]} />
      </SubSection>

      <SubSection title="Utilizzi dell'export">
        <FeatureList items={[
          "Condividere con il medico prima di una visita",
          "Portare in ospedale in caso di emergenza",
          "Conservare come backup dei propri dati",
          "Analizzare i dati con software esterni",
          "Trasferire dati a un nuovo sistema",
        ]} />
      </SubSection>

      <Tip>
        Esporta regolarmente il tuo fascicolo e conserva una copia in un luogo sicuro.
        Il PDF può essere salvato su cloud o stampato.
      </Tip>
    </div>
  );
}

function SettingsSection() {
  return (
    <div>
      <SectionTitle>Impostazioni</SectionTitle>

      <p className="text-gray-600 mb-6">
        Le impostazioni ti permettono di personalizzare Health Assistant, gestire
        le integrazioni e amministrare il sistema.
      </p>

      <HelpImage
        src="/help/settings.png"
        alt="Screenshot della sezione Impostazioni"
        caption="Configurazione completa del sistema"
      />

      <SubSection title="Impostazioni Utente">
        <p>
          Ogni utente può personalizzare la propria esperienza:
        </p>
        <FeatureList items={[
          "Scelta del provider LLM preferito",
          "Configurazione del modello AI",
          "Preferenze di notifica",
          "Cambio password",
        ]} />
      </SubSection>

      <SubSection title="Impostazioni Admin (solo amministratori)">
        <p>
          Gli amministratori hanno accesso a funzionalità avanzate:
        </p>

        <h4 className="font-medium text-gray-800 mt-4 mb-2">Tab LLM</h4>
        <FeatureList items={[
          "Configurazione provider (Ollama, Anthropic, OpenAI)",
          "Selezione modello predefinito",
          "Gestione API keys",
          "Routing automatico per immagini e documenti",
        ]} />

        <h4 className="font-medium text-gray-800 mt-4 mb-2">Tab Integrazioni</h4>
        <FeatureList items={[
          "Configurazione credenziali Strava",
          "Altre integrazioni future",
        ]} />

        <h4 className="font-medium text-gray-800 mt-4 mb-2">Tab Database</h4>
        <FeatureList items={[
          "Statistiche database (dimensione, tabelle)",
          "Creazione backup manuale",
          "Ripristino da backup",
          "Gestione file di backup",
        ]} />

        <h4 className="font-medium text-gray-800 mt-4 mb-2">Tab Utenti</h4>
        <FeatureList items={[
          "Lista utenti con statistiche",
          "Visualizzazione spazio disco per utente",
          "Forzare cambio password",
          "Eliminazione utenti",
        ]} />
      </SubSection>

      <SubSection title="Gestione utenti">
        <p>
          L'amministratore può gestire gli utenti del sistema:
        </p>
        <FeatureList items={[
          "Visualizzare tutti gli utenti registrati",
          "Vedere statistiche di utilizzo (documenti, conversazioni)",
          "Forzare il cambio password al prossimo login",
          "Eliminare utenti e tutti i loro dati",
        ]} />
        <Warning>
          L'eliminazione di un utente rimuove permanentemente tutti i suoi dati,
          inclusi documenti, conversazioni e parametri. Questa azione è irreversibile.
        </Warning>
      </SubSection>

      <SubSection title="Backup e restore">
        <p>
          È fondamentale mantenere backup regolari dei dati:
        </p>
        <StepList steps={[
          {
            title: "Configura il percorso backup",
            description: "Specifica dove salvare i file di backup sul server.",
          },
          {
            title: "Crea backup regolari",
            description: "Clicca 'Crea Backup' per salvare lo stato attuale del database.",
          },
          {
            title: "Gestisci i backup",
            description: "Visualizza, ripristina o elimina i backup esistenti.",
          },
        ]} />
        <Tip>
          Configura un backup automatico giornaliero usando cron o systemd timer
          per non rischiare di perdere dati importanti.
        </Tip>
      </SubSection>

      <SubSection title="Provider LLM">
        <div className="space-y-4 my-4">
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="font-semibold text-gray-900 mb-2">Ollama (Locale)</h4>
            <p className="text-sm text-gray-600">
              Esegue i modelli AI direttamente sul tuo server. Massima privacy,
              nessun dato inviato all'esterno. Richiede hardware adeguato (GPU consigliata).
            </p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="font-semibold text-gray-900 mb-2">Anthropic Claude</h4>
            <p className="text-sm text-gray-600">
              Modelli Claude di Anthropic. Eccellente per analisi di documenti e
              ragionamento complesso. Richiede API key.
            </p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="font-semibold text-gray-900 mb-2">OpenAI GPT</h4>
            <p className="text-sm text-gray-600">
              Modelli GPT di OpenAI. Versatile e affidabile. Richiede API key.
            </p>
          </div>
        </div>
      </SubSection>
    </div>
  );
}
